<!--
  BannerModal.vue
  배너 추가/수정 모달 컴포넌트
  
  주요 기능:
  - 배너 정보 입력/수정
  - 이미지 업로드
  - 노출 기간 설정
  - 폼 유효성 검사
-->
<template>
  <a-modal
    :open="open"
    :confirm-loading="loading"
    :footer="null"
    @cancel="handleCancel"
    width="800px"
    class="banner-modal"
  >
    <template #title>
      <div class="modal-title">
        {{ isEdit ? '배너 수정' : '배너 추가' }}
      </div>
    </template>
    
    <div class="modal-content">
      <a-form
        ref="formRef"
        :model="formData"
        :rules="formRules"
        layout="vertical"
        @finish="handleSubmit"
      >
        <!-- 배너명 -->
        <a-form-item
          label="배너명"
          name="bannerName"
          class="form-item"
        >
          <a-input
            v-model:value="formData.bannerName"
            placeholder="배너명을 입력해주세요"
            :maxlength="100"
            show-count
          />
        </a-form-item>

        <!-- 링크 URL -->
        <a-form-item
          label="링크 URL (선택사항)"
          name="linkUrl"
          class="form-item"
        >
          <a-input
            v-model:value="formData.linkUrl"
            placeholder="https://example.com (선택사항)"
            :maxlength="500"
          />
        </a-form-item>

        <!-- 이미지 업로드 -->
        <a-form-item
          label="배너 이미지"
          name="bannerLink"
          class="form-item"
        >
          <div class="image-upload-container">
            <image-upload
              label="배너 이미지"
              v-model:value="formData.bannerLink"
              @change="handleImageChange"
              container-name="certilife"
              :upload-path="uploadPath"
              @upload-success="handleImageUploadSuccess"
              @upload-error="handleImageUploadError"
            />
          </div>
        </a-form-item>

        <!-- 노출 기간 -->
        <div class="form-row">
          <a-form-item
            label="노출 시작일"
            name="displayStartDate"
            class="form-item half-width"
          >
            <a-date-picker
              v-model:value="formData.displayStartDate"
              format="YYYY-MM-DD"
              placeholder="시작일 선택"
              style="width: 100%"
            />
          </a-form-item>

          <a-form-item
            label="노출 종료일"
            name="displayEndDate"
            class="form-item half-width"
          >
            <a-date-picker
              v-model:value="formData.displayEndDate"
              format="YYYY-MM-DD"
              placeholder="종료일 선택"
              style="width: 100%"
            />
          </a-form-item>
        </div>

        <!-- 노출 여부 및 정렬 순서 -->
        <div class="form-row">
          <a-form-item
            label="노출 여부"
            name="isVisible"
            class="form-item half-width"
          >
            <a-switch
              :checked="formData.isVisible === 'Y'"
              @change="(checked) => formData.isVisible = checked ? 'Y' : 'N'"
              checked-children="노출"
              un-checked-children="비노출"
            />
          </a-form-item>

          <a-form-item
            label="정렬 순서"
            name="order"
            class="form-item half-width"
          >
            <a-input-number
              v-model:value="formData.order"
              :min="1"
              :max="999"
              placeholder="정렬 순서"
              style="width: 100%"
            />
          </a-form-item>
        </div>

        <!-- 설명 (선택사항) -->
        <a-form-item
          label="설명"
          name="description"
          class="form-item"
        >
          <a-textarea
            v-model:value="formData.description"
            placeholder="배너에 대한 설명을 입력해주세요 (선택사항)"
            :rows="3"
            :maxlength="500"
            show-count
          />
        </a-form-item>
      </a-form>

      <!-- 모달 푸터 -->
      <div class="modal-footer">
        <a-button @click="handleCancel">취소</a-button>
        <a-button 
          type="primary" 
          :loading="loading"
          @click="handleSubmit"
          class="yellow-button"
        >
          {{ isEdit ? '수정' : '추가' }}
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { ref, reactive, watch, getCurrentInstance } from 'vue';
import { message } from 'ant-design-vue';
import dayjs from 'dayjs';
import ImageUpload from "@/views/components/ImageUpload.vue";

export default {
  name: 'BannerModal',
  components: {
    ImageUpload
  },
  props: {
    open: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    bannerData: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['ok', 'cancel'],
  setup(props, { emit }) {
    // API 인스턴스
    const instance = getCurrentInstance();
    const apiInstance = instance?.proxy?.$apiInstance;

    // 폼 참조
    const formRef = ref();
    
    // 업로드 경로
    const uploadPath = "banners";

    // 폼 데이터
    const formData = reactive({
      bannerName: '',
      linkUrl: '',
      bannerLink: '',
      displayStartDate: null,
      displayEndDate: null,
      isVisible: 'Y',
      order: 1,
      description: ''
    });

    // 폼 유효성 검사 규칙
    const formRules = {
      bannerName: [
        { required: true, message: '배너명을 입력해주세요.', trigger: 'blur' },
        { min: 2, max: 100, message: '배너명은 2~100자 사이여야 합니다.', trigger: 'blur' }
      ],
      linkUrl: [
        {
          validator: (rule, value) => {
            // 빈 값이면 검증 통과 (선택사항)
            if (!value || value.trim() === '') {
              return Promise.resolve();
            }
            // 값이 있으면 URL 형식 검증
            if (!/^https?:\/\/.+/.test(value)) {
              return Promise.reject('올바른 URL 형식을 입력해주세요. (http:// 또는 https://로 시작)');
            }
            return Promise.resolve();
          },
          trigger: 'blur'
        }
      ],
      displayStartDate: [
        { required: true, message: '노출 시작일을 선택해주세요.', trigger: 'change' }
      ],
      displayEndDate: [
        { required: true, message: '노출 종료일을 선택해주세요.', trigger: 'change' },
        {
          validator: (rule, value) => {
            if (value && formData.displayStartDate && dayjs(value).isBefore(dayjs(formData.displayStartDate))) {
              return Promise.reject('종료일은 시작일보다 늦어야 합니다.');
            }
            return Promise.resolve();
          },
          trigger: 'change'
        }
      ],
      order: [
        { required: true, message: '정렬 순서를 입력해주세요.', trigger: 'blur' },
        { type: 'number', min: 1, max: 999, message: '정렬 순서는 1~999 사이여야 합니다.', trigger: 'blur' }
      ]
    };

    // 폼 데이터 초기화
    const initFormData = () => {
      if (props.isEdit && props.bannerData) {
        Object.assign(formData, {
          bannerName: props.bannerData.bannerName || '',
          linkUrl: props.bannerData.linkUrl || '',
          bannerLink: props.bannerData.bannerLink || '',
          displayStartDate: props.bannerData.displayStartDate ? dayjs(props.bannerData.displayStartDate) : null,
          displayEndDate: props.bannerData.displayEndDate ? dayjs(props.bannerData.displayEndDate) : null,
          isVisible: props.bannerData.isVisible !== undefined ? props.bannerData.isVisible : 'Y',
          order: props.bannerData.order || 1,
          description: props.bannerData.description || ''
        });
      } else {
        Object.assign(formData, {
          bannerName: '',
          linkUrl: '',
          bannerLink: '',
          displayStartDate: null,
          displayEndDate: null,
          isVisible: 'Y',
          order: 1,
          description: ''
        });
      }
    };

    // 이미지 변경 처리
    const handleImageChange = (imageData) => {
      formData.bannerLink = imageData;
    };

    // 이미지 업로드 성공 핸들러
    const handleImageUploadSuccess = (response) => {
      try {
        console.log("배너 이미지 업로드 성공", response);
        if (response.data && response.data.results && response.data.results.length > 0) {
          formData.bannerLink = response.data.results[0].url;
        }
      } catch (error) {
        console.error("이미지 URL 처리 오류", error);
      }
    };

    // 업로드 오류 핸들러
    const handleImageUploadError = (error) => {
      console.error("이미지 업로드 실패", error);
      message.error("이미지 업로드에 실패했습니다.");
    };

    // 폼 제출
    const handleSubmit = async () => {
      if (!formRef.value) return;
      
      try {
        await formRef.value.validate();
        
        // 이미지가 없으면 경고
        if (!formData.bannerLink) {
          message.warning('배너 이미지를 업로드해주세요.');
          return;
        }
        
        const submitData = {
          ...formData,
          linkUrl: formData.linkUrl || '', // 빈 문자열 허용
          displayStartDate: formData.displayStartDate ? formData.displayStartDate.format('YYYY-MM-DD') : null,
          displayEndDate: formData.displayEndDate ? formData.displayEndDate.format('YYYY-MM-DD') : null
        };
        
        // null과 undefined만 제거하고, 빈 문자열은 유지
        const cleanedData = Object.keys(submitData).reduce((acc, key) => {
          if (key === 'linkUrl') {
            // linkUrl은 빈 문자열도 허용
            acc[key] = submitData[key] || '';
          } else if (submitData[key] !== null && submitData[key] !== undefined) {
            acc[key] = submitData[key];
          }
          return acc;
        }, {});
        
        emit('ok', cleanedData);
      } catch (error) {
        console.error('Form validation failed:', error);
      }
    };

    // 모달 취소
    const handleCancel = () => {
      emit('cancel');
    };

    // props 변경 감지
    watch(() => props.open, (newVal) => {
      if (newVal) {
        initFormData();
      }
    });

    watch(() => props.bannerData, () => {
      if (props.open) {
        initFormData();
      }
    }, { deep: true });

    return {
      formRef,
      formData,
      formRules,
      uploadPath,
      handleImageChange,
      handleImageUploadSuccess,
      handleImageUploadError,
      handleSubmit,
      handleCancel
    };
  }
};
</script>

<style scoped lang="scss">
.modal-title {
  font-size: 16px;
  font-weight: bold;
}

.modal-content {
  padding: 20px;
  border: none;
  background: #fff;
}

.form-item {
  margin-bottom: 24px;
  
  :deep(.ant-form-item-label) {
    padding: 0 0 8px;
    
    label {
      color: #000;
      font-size: 14px;
      height: auto;
      font-weight: 500;
    }
  }
  
  :deep(.ant-form-item-control-input) {
    .ant-input, .ant-input-number, .ant-select-selector {
      height: 48px;
      border-radius: 8px;
      border: 1px solid #e5e7eb;
      background: #f9fafb;
      
      &:hover, &:focus {
        border-color: #000;
        box-shadow: none;
      }
    }
    
    .ant-picker {
      height: 48px;
      border-radius: 8px;
      border: 1px solid #e5e7eb;
      background: #f9fafb;
      width: 100%;
      
      &:hover, &.ant-picker-focused {
        border-color: #000;
        box-shadow: none;
      }
    }
  }
}

.form-row {
  display: flex;
  gap: 16px;
  
  .half-width {
    flex: 1;
  }
}

// 이미지 업로드 컨테이너 스타일
.image-upload-container {
  margin-bottom: 16px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
}

:deep(.yellow-button) {
  background-color: #FFCC32 !important;
  border-color: #FFCC32 !important;
  color: #000 !important;
  
  &:hover {
    background-color: #E6B82E !important;
    border-color: #E6B82E !important;
  }
  
  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}

// 반응형 스타일
@media screen and (max-width: 768px) {
  .modal-content {
    padding: 16px;
  }
  
  .form-row {
    flex-direction: column;
    gap: 0;
  }
  
  .image-upload-container {
    .image-preview, 
    .upload-area {
      width: 100%;
      height: 150px;
    }
  }
  
  .modal-footer {
    flex-direction: column;
    
    .ant-btn {
      width: 100%;
    }
  }
}
</style> 